import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  randomPickerColor,
  Switch,
  VisualPicker,
  type Color,
  type GlyphName,
} from "@salestrak/ui";

import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { useDebouncedCallback } from "#app/lib/hooks/use-debounced-callback";
import {
  TEAM_HANDLE_MAX_LENGTH,
  TEAM_NAME_MAX_LENGTH,
  TEAM_NAME_MIN_LENGTH,
  isTeamHandle,
  normalizeTeamHandleInput,
  teamNameError,
  toTeamHandle,
} from "#app/lib/team-handle";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CreateTeamDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (teamId: string) => void;
}>;

// ---------------------------------------------------------------------------
// Component — THE team create flow (every surface opens this dialog),
// mirroring CreateWorkflowDialog. One Dialog at every breakpoint. The icon
// picker behaves like the team edit page's Name & Icon row.
// ---------------------------------------------------------------------------

/** How long the handle field waits after the last keystroke before the
 *  uniqueness check runs. */
const HANDLE_VALIDATE_DELAY_MS = 250;

export function CreateTeamDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateTeamDialogProps) {
  const { createTeam, validateTeamKey } = useTeamMutations();

  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [autoHandle, setAutoHandle] = useState(true);
  // Default avatar = letter monogram (icon null) on a random palette color
  // (greys excluded — they read as "unset" on an avatar tile). Picking a
  // glyph in the picker overrides the letter.
  const [iconName, setIconName] = useState<GlyphName | null>(null);
  const [iconColor, setIconColor] = useState<Color>(() => randomPickerColor());
  const [isPrivate, setIsPrivate] = useState(false);

  const [nameError, setNameError] = useState("");
  // The length rule speaks once the name field is left (or at once past the
  // maximum), so the first keystrokes are not met with an error.
  const [nameBlurred, setNameBlurred] = useState(false);
  const [handleError, setHandleError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Key validation debounce

  // Reset on close — re-rolling the color so the next create doesn't
  // inherit this one's swatch (randomTeamColor excludes the previous pick).
  useEffect(() => {
    if (!open) {
      setName("");
      setHandle("");
      setAutoHandle(true);
      setIconName(null);
      setIconColor((prev) => randomPickerColor(prev));
      setIsPrivate(false);
      setNameError("");
      setNameBlurred(false);
      setHandleError("");
      setSubmitting(false);
    }
  }, [open]);

  // Auto-populate the handle from the name until the user edits it directly.
  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      setNameError("");
      if (autoHandle) {
        setHandle(toTeamHandle(value));
        setHandleError("");
      }
    },
    [autoHandle],
  );

  // A hyphen typed at the end survives (normalizeTeamHandleInput keeps it),
  // so "field-" is a state the user can pass through on the way to
  // "field-crew"; submit sends the trimmed form.
  const applyHandle = useCallback((value: string) => {
    setAutoHandle(false);
    setHandle(normalizeTeamHandleInput(value));
    setHandleError("");
  }, []);

  // The submitted form of what is in the field: a hyphen the user is typing
  // through ("field-") is trimmed off before it is checked or sent.
  const submittedHandle = toTeamHandle(handle);

  // Debounced handle uniqueness validation: the server is asked once the
  // handle stops changing, never per keystroke.
  const { debounced: validateHandle, cancel: cancelValidate } = useDebouncedCallback(
    async (candidate: string) => {
      try {
        const available = await validateTeamKey(candidate);
        setHandleError(available ? "" : "Handle already in use");
      } catch {
        // Ignore validation errors
      }
    },
    HANDLE_VALIDATE_DELAY_MS,
  );
  useEffect(() => {
    if (!submittedHandle || !isTeamHandle(submittedHandle)) {
      cancelValidate();
      setHandleError("");
      return;
    }
    validateHandle(submittedHandle);
  }, [submittedHandle, validateHandle, cancelValidate]);

  const nameRuleError = teamNameError(name);
  const shownNameError =
    nameError ||
    (nameBlurred || name.trim().length > TEAM_NAME_MAX_LENGTH ? nameRuleError : null);

  const canSubmit =
    !nameRuleError &&
    (!submittedHandle || isTeamHandle(submittedHandle)) &&
    !handleError &&
    !submitting;

  const handleSubmit = useCallback(async () => {
    const ruleError = teamNameError(name);
    if (ruleError) {
      setNameError(ruleError);
      return;
    }

    setSubmitting(true);
    try {
      // createTeam toasts success/failure itself and returns null on error.
      const result = await createTeam({
        teamName: name.trim(),
        teamKey: submittedHandle || undefined,
        icon: { icon: iconName, color: iconColor },
        private: isPrivate,
      }, "create_team_dialog");

      if (result && "teamId" in result) {
        onOpenChange(false);
        onCreated?.(result.teamId);
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    name,
    submittedHandle,
    iconName,
    iconColor,
    isPrivate,
    createTeam,
    onOpenChange,
    onCreated,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        dialogOptions={{
          primary: { label: "Create", onClick: handleSubmit, disabled: !canSubmit, loading: submitting },
          cancel: { disabled: submitting },
        }}
      >
        <DialogHeader>
          <DialogTitle>Create Team</DialogTitle>
        </DialogHeader>
        <Box
          as="form"
          gap="lg"
          paddingX="lg"
          // Bottom padding keeps the last row off the footer's border —
          // DialogPanel would supply this, but this form composes a bare Box.
          paddingBottom="lg"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) void handleSubmit();
          }}
        >
          <Field invalid={!!shownNameError}>
            <FieldLabel>Name</FieldLabel>
            {/* Field's root is items-start, so a bare row hugs its content —
                the row must claim the column width for the input to span. */}
            <Box direction="row" gap="sm" align="center" fill="width">
              <VisualPicker
                icon={iconName}
                color={iconColor}
                onIconChange={setIconName}
                onColorChange={setIconColor}
                // A team's default appearance IS its letter tile; the seeded
                // color stays, because a fresh random hue would read as a
                // second, unasked-for change.
                onReset={iconName === null ? undefined : () => setIconName(null)}
                size="sm"
                // Hairline control, appearance in the ink — the face the
                // team's Name & Icon settings row wears, so a team is
                // authored the same way wherever it is authored.
                variant="outline"
                // The letter option tracks the name field live; "T"
                // (for Team) stands in until the first character is typed.
                monogram={name || "T"}
              />
              <Box fill="flex" shrink>
                <Input
                  placeholder="Team name..."
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  onBlur={() => setNameBlurred(true)}
                  autoFocus
                />
              </Box>
            </Box>
            {shownNameError ? (
              <FieldError match>{shownNameError}</FieldError>
            ) : (
              <FieldDescription>
                {TEAM_NAME_MIN_LENGTH} to {TEAM_NAME_MAX_LENGTH} characters
              </FieldDescription>
            )}
          </Field>

          <Field invalid={!!handleError}>
            <FieldLabel>Team handle</FieldLabel>
            <Input
              placeholder="sales-team"
              value={handle}
              onChange={(e) => applyHandle(e.target.value)}
              maxLength={TEAM_HANDLE_MAX_LENGTH}
            />
            {handleError ? <FieldError match>{handleError}</FieldError> : null}
          </Field>

          {/* Same Field grammar as the inputs above — the label/description
              column with the switch trailing; Base UI associates the label,
              so pressing "Private team" toggles it. */}
          <Field>
            <Box
              direction="row"
              align="center"
              justify="between"
              gap="lg"
              fill="width"
            >
              <Box gap="2xs" fill="flex" shrink>
                <FieldLabel>Private team</FieldLabel>
                <FieldDescription>
                  Only visible to team members and admins
                </FieldDescription>
              </Box>
              <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
            </Box>
          </Field>
        </Box>
      </DialogPopup>
    </Dialog>
  );
}
